import { Client, ProductGroup } from '@app/core/models/client';
import {
  computeProductGroupBranding,
  ComputeBrandingClient,
  ProductGroup as FeedApiProductGroup,
} from '@relaynetwork/rn-node-feed-api-types';

/**
 * Flat, already-gated prop bag shared by all four password screen previews
 * (Create, Log In, Forgot, Change). Mirrors resolve-verification-preview-props.ts
 * so the three preview call sites (PDF preview, config editor, Spanish
 * translations editor) stay in lockstep. Every gating/branding decision is made
 * via the shared `computeProductGroupBranding` function so the previews track the
 * real branding endpoint instead of re-deriving the logic off `productGroup.*`.
 */
export interface PasswordPagesPreviewProps {
  pswMessageText: string;
  pswButtonLabel: string;
  pswTCS: string;
  pswTCMandatory: boolean;
  consentContent?: string;
  logoUrl: string;
  color: string;
  productGroupName: string;
  showProductGroup: boolean;
  productGroupDescription: string;
  fdic?: ProductGroup['fdic'];
  showFdic: boolean;
  showLogo: boolean;
  showTitleBar: boolean;
  language: string;
  /**
   * Raw es overrides (`i18n.es`) forwarded to the preview component, which
   * registers them as the DS i18next `es` bundle so the `SetPassword` organism
   * resolves `translations.password.*` itself via `t(key, <base>)`. That's why
   * pswMessageText/pswButtonLabel/pswTCS are passed as the *base* (English)
   * values here — i18next applies the es override, or falls back to the base,
   * at render time.
   */
  i18nEs?: NonNullable<ProductGroup['i18n']>['es'];
}

export function resolvePasswordPagesPreviewProps(
  client: Client,
  productGroup: ProductGroup,
  language: string = 'en',
): PasswordPagesPreviewProps {
  // Portal's Angular models are structurally similar to but not identical to the
  // feed-api TypeBox types; bridge only at this boundary via a single structural
  // cast, mirroring resolve-verification-preview-props.ts.
  const feedProductGroup = productGroup as unknown as FeedApiProductGroup;

  const brandingClient: ComputeBrandingClient = {
    verification: { enabled: client.verification?.enabled === 'true' },
    fdic_enabled: client.fdic_enabled,
    two_way_config: {
      translations_enabled: client.two_way_config?.translations_enabled,
    },
  };

  const branding = computeProductGroupBranding(
    brandingClient,
    feedProductGroup,
  );

  return {
    // Base (English) values — the DS `SetPassword` organism resolves the es
    // override itself from the registered `i18nEs` bundle.
    pswMessageText: branding.pswMessageText ?? '',
    pswButtonLabel: branding.pswButtonLabel ?? '',
    pswTCS: branding.pswTCS ?? '',
    pswTCMandatory: branding.pswTCMandatory ?? false,
    consentContent: branding.consentTSCS,
    // Sourced from the portal's own hostnamed model, not `branding.iconUrl`
    // (feed-api's relative-prefixed value) — same treatment as verification.
    logoUrl: productGroup.branding.icon_s3_url,
    color: branding.color,
    productGroupName: productGroup.name,
    showProductGroup: !branding.hideDisplayName,
    productGroupDescription: branding.description ?? productGroup.description,
    fdic: productGroup.fdic,
    showFdic: productGroup.fdic?.show_signage_on_authentication ?? false,
    showLogo: branding.showLogo ?? true,
    showTitleBar: branding.showTitleBar ?? true,
    language,
    i18nEs: productGroup.i18n?.es,
  };
}
